/**
 * Sentence layout — glyphs arranged into a block of readable text.
 *
 * The output is one flat set of textured quads (six vertices per glyph, two
 * triangles), plus a per-vertex `order` that carries which word and which
 * letter a glyph belongs to. That is what lets the shader stagger assembly by
 * word and letter without any CPU work per frame: letters fly in and land in
 * reading order, which is what makes it read as something being written rather
 * than something fading in.
 *
 * Coordinates are in EMS, x rightward from the block's left edge and y upward
 * from its last baseline. The renderer scales ems to world units, so the same
 * layout is legible at any distance.
 */

import { glyph, GLYPH_EM_LEFT, GLYPH_EM_RIGHT, GLYPH_EM_TOP, GLYPH_EM_BOTTOM } from './glyphs.js';

export interface TextCloud {
  /** Per-vertex em-local xy, 6 vertices per glyph. */
  pos: Float32Array;
  /** Per-vertex UV into the glyph atlas. */
  uv: Float32Array;
  /** Per-vertex 0..1 assembly order, in reading sequence — identical across a
   *  glyph's 6 vertices, so the letter moves as one rigid shape. */
  order: Float32Array;
  /** Per-vertex random seed, identical across a glyph's 6 vertices, so its
   *  flight path is fixed but differs letter to letter. */
  seed: Float32Array;
  /** Vertex count. Always a multiple of 6. */
  count: number;
  /** Block extent in ems. */
  width: number;
  height: number;
  /** Line count, for callers that want to size the anchor. */
  lines: number;
}

export interface LayoutOptions {
  /** Wrap width in ems. */
  maxWidth?: number;
  /** Baseline separation in ems. */
  lineHeight?: number;
  /** Extra tracking in ems, added per character. */
  tracking?: number;
}

const EMPTY: TextCloud = {
  pos: new Float32Array(0),
  uv: new Float32Array(0),
  order: new Float32Array(0),
  seed: new Float32Array(0),
  count: 0,
  width: 0,
  height: 0,
  lines: 0,
};

/** Cheap deterministic hash, so two glyphs at the same layout always fly the
 *  same way — useful for anything that re-lays the same text out twice. */
function hash(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

export function layoutText(text: string, opts: LayoutOptions = {}): TextCloud {
  const maxWidth = opts.maxWidth ?? 14;
  const lineHeight = opts.lineHeight ?? 1.35;
  const tracking = opts.tracking ?? 0.02;

  const trimmed = text.trim();
  if (!trimmed) return EMPTY;

  // Break into words, then greedily fill lines. Words are the unit because the
  // assembly stagger is per word — a sentence that lands word by word reads as
  // dictation, letter by letter alone reads as a teletype.
  const words = trimmed.split(/\s+/);
  const measured = words.map((w) => ({
    w,
    width: [...w].reduce((s, ch) => s + glyph(ch).advance + tracking, 0),
  }));
  const spaceW = glyph(' ').advance + tracking;

  const lines: { w: string; width: number }[][] = [];
  let cur: { w: string; width: number }[] = [];
  let curW = 0;
  for (const m of measured) {
    const add = cur.length === 0 ? m.width : m.width + spaceW;
    if (cur.length > 0 && curW + add > maxWidth) {
      lines.push(cur);
      cur = [m];
      curW = m.width;
    } else {
      cur.push(m);
      curW += add;
    }
  }
  if (cur.length) lines.push(cur);

  const blockWidth = Math.max(...lines.map((l) => l.reduce((s, m, i) => s + m.width + (i ? spaceW : 0), 0)));
  const blockHeight = (lines.length - 1) * lineHeight + 1;

  // Two passes: count visible glyphs, then fill. Whitespace and anything the
  // font renders as nothing gets no quad.
  let totalGlyphs = 0;
  for (const line of lines) for (const m of line) for (const ch of m.w) if (glyph(ch).hasGlyph) totalGlyphs++;
  if (totalGlyphs === 0) return EMPTY;

  const pos = new Float32Array(totalGlyphs * 12); // 6 verts * 2 floats
  const uv = new Float32Array(totalGlyphs * 12);
  const order = new Float32Array(totalGlyphs * 6);
  const seed = new Float32Array(totalGlyphs * 6);

  let wordIndex = 0;
  const totalWords = measured.length;
  let gIndex = 0; // which glyph, for the vertex arrays
  let serial = 0; // running count of ALL characters incl. skipped, for seed variety

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li]!;
    // Baselines run downward from the top of the block; y is up, so the first
    // line sits highest.
    const baseY = (lines.length - 1 - li) * lineHeight;
    let penX = 0;
    for (let wi = 0; wi < line.length; wi++) {
      const m = line[wi]!;
      if (wi > 0) penX += spaceW;
      let gi = 0;
      const glyphsInWord = [...m.w].length;
      for (const ch of m.w) {
        const g = glyph(ch);
        // Order blends word position with letter position inside the word, so
        // words arrive in sequence and letters within a word arrive together
        // but not simultaneously.
        const wordT = totalWords > 1 ? wordIndex / (totalWords - 1) : 0;
        const letterT = glyphsInWord > 1 ? gi / (glyphsInWord - 1) : 0;
        const o = Math.min(1, wordT * 0.85 + letterT * 0.15 * (1 / Math.max(1, totalWords * 0.35)));

        if (g.hasGlyph) {
          const x0 = penX + GLYPH_EM_LEFT;
          const x1 = penX + GLYPH_EM_RIGHT;
          const yTop = baseY + GLYPH_EM_TOP;
          const yBot = baseY + GLYPH_EM_BOTTOM;
          const s = hash(serial + 0.5);

          // Two triangles: (TL,TR,BL) and (TR,BR,BL).
          const vp = gIndex * 12;
          const vu = gIndex * 12;
          const vo = gIndex * 6;
          pos.set(
            [x0, yTop, x1, yTop, x0, yBot, x1, yTop, x1, yBot, x0, yBot],
            vp,
          );
          uv.set(
            [g.u0, g.v0, g.u1, g.v0, g.u0, g.v1, g.u1, g.v0, g.u1, g.v1, g.u0, g.v1],
            vu,
          );
          for (let k = 0; k < 6; k++) {
            order[vo + k] = o;
            seed[vo + k] = s;
          }
          gIndex++;
        }
        penX += g.advance + tracking;
        gi++;
        serial++;
      }
      wordIndex++;
    }
  }

  return { pos, uv, order, seed, count: gIndex * 6, width: blockWidth, height: blockHeight, lines: lines.length };
}
